import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Stack,
  Button,
  TextField,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import dayjs from 'dayjs';
import useVoucher from '../../hooks/useVoucher';
import useVoucherProduct from '../../hooks/useVoucherProduct';
import { useAuth } from '../../context/authContext';
import LockIconWrapper from '../components/LockIconWrapper';
import { toast } from 'react-toastify';

function CreateVoucherModal({ open, onClose, onSuccess }) {
  const { createVoucher, getVouchersZipURL, getVoucherBranchEntertainments, isLoading, error } =
    useVoucher();
  const { getAllVoucherProducts, isLoading: isLoadingProducts } = useVoucherProduct();
  const { checkRightAny } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [voucherProducts, setVoucherProducts] = useState([]);
  const [selectedVoucherProduct, setSelectedVoucherProduct] = useState('');
  const [selectedSuggestedValue, setSelectedSuggestedValue] = useState('');
  const [formData, setFormData] = useState({
    voucherValue: '',
    paidValue: '',
    expirationDate: dayjs().add(1, 'year').format('YYYY-MM-DD'),
    creationComment: '',
    quantity: 1,
  });

  // Calculate max expiration date (3 years from now)
  const maxExpirationDate = dayjs().add(3, 'year').format('YYYY-MM-DD');
  const [validationErrors, setValidationErrors] = useState({});

  // Optional redemption restriction (branch + entertainments).
  const [branchEntertainments, setBranchEntertainments] = useState([]);
  const [restrictedBranchId, setRestrictedBranchId] = useState('');
  const [restrictedEntertainmentIds, setRestrictedEntertainmentIds] = useState([]);

  // ZIP download confirmation modal state
  const [zipConfirmOpen, setZipConfirmOpen] = useState(false);
  const [zipVoucherIds, setZipVoucherIds] = useState([]);
  const pendingCloseRef = useRef(false);

  // Fetch voucher products on mount
  useEffect(() => {
    const fetchVoucherProducts = async () => {
      try {
        const products = await getAllVoucherProducts();
        setVoucherProducts(products);
      } catch (error) {
        toast.error('Fehler beim Laden der Gutscheinprodukte');
        console.error('Error fetching voucher products:', error);
      }
    };

    if (open) {
      fetchVoucherProducts();
    }
  }, [open]);

  // Fetch branches + entertainments for the redemption restriction selectors
  useEffect(() => {
    const fetchBranchEntertainments = async () => {
      try {
        const result = await getVoucherBranchEntertainments();
        setBranchEntertainments(Array.isArray(result?.branches) ? result.branches : []);
      } catch (err) {
        setBranchEntertainments([]);
        toast.error('Fehler beim Laden der Filialen/Entertainments');
      }
    };

    if (open) {
      fetchBranchEntertainments();
    }
  }, [open]);

  // Entertainments available for the selected restricted branch
  const restrictedEntertainmentOptions = useMemo(() => {
    const branch = branchEntertainments.find(b => String(b._id) === String(restrictedBranchId));
    return Array.isArray(branch?.entertainments) ? branch.entertainments : [];
  }, [branchEntertainments, restrictedBranchId]);

  // Reset entertainment selection when the restricted branch changes
  const handleRestrictedBranchChange = branchId => {
    setRestrictedBranchId(branchId);
    setRestrictedEntertainmentIds([]);
  };

  // Get selected voucher product details
  const selectedProduct = useMemo(() => {
    return voucherProducts.find(product => product._id === selectedVoucherProduct);
  }, [voucherProducts, selectedVoucherProduct]);

  // Trigger ZIP download for batch-created vouchers
  const triggerBatchZipDownload = async ids => {
    if (!Array.isArray(ids) || ids.length < 2) return;
    try {
      const toastMessage = `Die Download-Anfrage für ${ids.length} PDFs wurde gespeichert`;
      const displayInfo = { Typ: 'Gutscheine ZIP Download', Anzahl: ids.length.toString() };
      const response = await getVouchersZipURL(
        ids,
        toastMessage,
        resp => {
          if (resp && resp.preSignedURL) window.open(resp.preSignedURL, '_blank');
        },
        displayInfo
      );
      if (response && response.preSignedURL) window.open(response.preSignedURL, '_blank');
    } catch (e) {
      toast.error('Fehler beim Erstellen des ZIP-Downloads');
      console.error('Batch ZIP download failed:', e);
    }
  };

  // Handle voucher product selection
  const handleVoucherProductChange = productId => {
    setSelectedVoucherProduct(productId);
    setSelectedSuggestedValue('');

    if (productId) {
      const product = voucherProducts.find(p => p._id === productId);
      if (
        product &&
        product.defaultValue &&
        (!product.suggestedValues || product.suggestedValues.length === 0)
      ) {
        // Only set default value directly if there are no suggested values
        setFormData(prev => ({ ...prev, voucherValue: product.defaultValue.toString() }));
      }
    }
  };

  // Handle suggested value selection
  const handleSuggestedValueChange = value => {
    setSelectedSuggestedValue(value);
    if (value === 'custom') {
      // Clear voucher value when custom is selected
      setFormData(prev => ({ ...prev, voucherValue: '' }));
    } else {
      // Set the selected suggested value
      setFormData(prev => ({ ...prev, voucherValue: value }));
    }
  };

  // Set default value when product changes and has suggested values
  useEffect(() => {
    if (
      selectedProduct &&
      selectedProduct.suggestedValues &&
      selectedProduct.suggestedValues.length > 0
    ) {
      if (
        selectedProduct.defaultValue &&
        selectedProduct.suggestedValues.includes(selectedProduct.defaultValue)
      ) {
        setSelectedSuggestedValue(selectedProduct.defaultValue.toString());
        setFormData(prev => ({ ...prev, voucherValue: selectedProduct.defaultValue.toString() }));
      } else if (selectedProduct.suggestedValues.length > 0) {
        // If no default value, select the first suggested value
        const firstValue = selectedProduct.suggestedValues[0];
        setSelectedSuggestedValue(firstValue.toString());
        setFormData(prev => ({ ...prev, voucherValue: firstValue.toString() }));
      }
    }
  }, [selectedProduct]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setValidationErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }

    // If user types in voucher value field and a product with suggested values is selected, switch to custom
    if (
      field === 'voucherValue' &&
      selectedProduct &&
      selectedProduct.suggestedValues &&
      selectedProduct.suggestedValues.length > 0 &&
      selectedProduct.allowCustomValue
    ) {
      setSelectedSuggestedValue('custom');
    }
  };

  const validateForm = () => {
    const errors = {};

    const gifted = parseFloat(formData.voucherValue || '0') || 0;
    const paid = parseFloat(formData.paidValue || '0') || 0;
    const total = gifted + paid;

    if (gifted < 0) {
      errors.voucherValue = 'Geschenkter Anteil darf nicht negativ sein';
    }

    if (paid < 0) {
      errors.paidValue = 'Bezahlter Anteil darf nicht negativ sein';
    }

    if (total <= 0) {
      errors.voucherValue = 'Gesamtwert muss größer als 0 sein';
      if (checkRightAny('voucher:special:createPaidVouchers')) {
        errors.paidValue = errors.voucherValue;
      }
    } else {
      // Check voucher product constraints if a product is selected
      if (selectedProduct) {
        if (selectedProduct.minValue && total < selectedProduct.minValue) {
          errors.voucherValue = `Gesamtwert muss mindestens ${selectedProduct.minValue.toFixed(2)}€ betragen`;
          if (checkRightAny('voucher:special:createPaidVouchers'))
            errors.paidValue = errors.voucherValue;
        } else if (selectedProduct.maxValue && total > selectedProduct.maxValue) {
          errors.voucherValue = `Gesamtwert darf maximal ${selectedProduct.maxValue.toFixed(2)}€ betragen`;
          if (checkRightAny('voucher:special:createPaidVouchers'))
            errors.paidValue = errors.voucherValue;
        } else if (total > 2000) {
          errors.voucherValue = 'Gesamtwert darf maximal 2000€ betragen';
          if (checkRightAny('voucher:special:createPaidVouchers'))
            errors.paidValue = errors.voucherValue;
        }
      } else {
        // No product selected, only check global max
        if (total > 2000) {
          errors.voucherValue = 'Gesamtwert darf maximal 2000€ betragen';
          if (checkRightAny('voucher:special:createPaidVouchers'))
            errors.paidValue = errors.voucherValue;
        }
      }
    }

    if (!formData.expirationDate) {
      errors.expirationDate = 'Ablaufdatum ist erforderlich';
    } else if (dayjs(formData.expirationDate).isBefore(dayjs(), 'day')) {
      errors.expirationDate = 'Ablaufdatum darf nicht in der Vergangenheit liegen';
    } else if (dayjs(formData.expirationDate).isAfter(dayjs().add(3, 'year').endOf('year'))) {
      errors.expirationDate = 'Ablaufdatum darf maximal 3 Jahre in der Zukunft liegen';
    }

    if (!selectedVoucherProduct) {
      errors.selectedVoucherProduct = 'Bitte wähle ein Produkt aus';
    }

    if (!formData.creationComment.trim()) {
      errors.creationComment = 'Erstellungskommentar ist erforderlich';
    } else if (formData.creationComment.trim().length < 10) {
      errors.creationComment = 'Erstellungskommentar muss mindestens 10 Zeichen lang sein';
    }

    if (activeTab === 1) {
      if (
        !formData.quantity ||
        parseInt(formData.quantity) < 1 ||
        parseInt(formData.quantity) > 10000
      ) {
        errors.quantity = 'Anzahl muss zwischen 1 und 10000 liegen';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const submitData = {
      voucherValue: parseFloat(formData.voucherValue || '0') || 0,
      paidValue: checkRightAny('voucher:special:createPaidVouchers')
        ? parseFloat(formData.paidValue || '0') || 0
        : 0,
      expirationDate: formData.expirationDate,
      creationComment: formData.creationComment,
      quantity: activeTab === 1 ? parseInt(formData.quantity) : 1,
      relatedVoucherProductId: selectedVoucherProduct || null,
      restrictedBranches: restrictedBranchId ? [restrictedBranchId] : [],
      restrictedEntertainments: restrictedBranchId ? restrictedEntertainmentIds : [],
    };

    const quantity = activeTab === 1 ? parseInt(formData.quantity) : 1;
    const toastMessage = `Die Erstellungsanfrage für ${quantity} Gutschein${
      quantity > 1 ? 'e' : ''
    } wurde gespeichert und kann später in den offenen Anfragen bestätigt werden`;

    const displayInfo = {
      Typ: 'Gutschein erstellen',
      Anzahl: quantity.toString(),
      Geschenkt: `${submitData.voucherValue.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} €`,
      Bezahlt: `${submitData.paidValue.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} €`,
      Summe: `${(submitData.voucherValue + submitData.paidValue).toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} €`,
      Ablaufdatum: dayjs(submitData.expirationDate).format('DD.MM.YYYY'),
      Produkt: selectedProduct?.name || 'Kein Produkt',
      Einlösbar: restrictedBranchId
        ? `${branchEntertainments.find(b => String(b._id) === String(restrictedBranchId))?.name || 'Filiale'}${
            restrictedEntertainmentIds.length > 0
              ? ` – ${restrictedEntertainmentOptions
                  .filter(e => restrictedEntertainmentIds.includes(e._id))
                  .map(e => e.longName)
                  .join(', ')}`
              : ' – Alle Entertainments'
          }`
        : 'Überall',
      Kommentar: submitData.creationComment || '-',
    };

    const onStored = async response => {
      // When the stored request is executed and returns a response, handle the success
      if (response) {
        onSuccess?.(response);
        const ids = Array.isArray(response?.vouchers)
          ? response.vouchers.map(v => v.voucherId)
          : null;
        if (ids && ids.length > 1) {
          setZipVoucherIds(ids);
          pendingCloseRef.current = true;
          setZipConfirmOpen(true);
        } else {
          handleClose();
        }
      }
    };

    try {
      const result = await createVoucher(submitData, toastMessage, onStored, displayInfo);
      if (result) {
        onSuccess?.(result);
        const ids = Array.isArray(result?.vouchers) ? result.vouchers.map(v => v.voucherId) : null;
        if (ids && ids.length > 1) {
          setZipVoucherIds(ids);
          pendingCloseRef.current = true;
          setZipConfirmOpen(true);
        } else {
          handleClose();
        }
      }
      // If result is null, it means the request was stored due to insufficient rights
      // No need to show an error message as useHttp handles this
    } catch (error) {
      toast.error('Fehler beim Erstellen des Gutscheins');
      console.error('Unexpected error during voucher creation:', error);
    }
  };

  const handleZipConfirm = async () => {
    setZipConfirmOpen(false);
    if (zipVoucherIds.length > 1) {
      await triggerBatchZipDownload(zipVoucherIds);
    }
    setZipVoucherIds([]);
    if (pendingCloseRef.current) {
      pendingCloseRef.current = false;
      handleClose();
    }
  };

  const handleZipCancel = () => {
    setZipConfirmOpen(false);
    setZipVoucherIds([]);
    if (pendingCloseRef.current) {
      pendingCloseRef.current = false;
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      voucherValue: '',
      paidValue: '',
      expirationDate: dayjs().add(2, 'year').format('YYYY-MM-DD'),
      creationComment: '',
      quantity: 1,
    });
    setSelectedVoucherProduct('');
    setSelectedSuggestedValue('');
    setRestrictedBranchId('');
    setRestrictedEntertainmentIds([]);
    setValidationErrors({});
    setActiveTab(0);
    onClose();
  };

  return (
    <>
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby='create-voucher-modal'
      aria-describedby='create-voucher-description'
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          overflow: 'auto',
          borderRadius: '10px',
        }}
      >
        <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
          <Typography variant='h5'>Gutschein erstellen</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label='Einfacher Gutschein' />
          {checkRightAny('voucher:special:batch') && (
            <Tab
              label={
                <LockIconWrapper right='voucher:special:batch'>Mehrere Gutscheine</LockIconWrapper>
              }
            />
          )}
        </Tabs>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          {/* Voucher Product Selection */}
          <FormControl fullWidth error={!!validationErrors.selectedVoucherProduct}>
            <InputLabel>Produkt *</InputLabel>
            <Select
              value={selectedVoucherProduct}
              onChange={e => handleVoucherProductChange(e.target.value)}
              label='Produkt'
              disabled={isLoadingProducts}
              required
            >
              <MenuItem value=''>
                <em>Kein Produkt zugeordnet</em>
              </MenuItem>
              {voucherProducts.map(product => (
                <MenuItem key={product._id} value={product._id}>
                  {product.name} {product.branchId && `(${product.branchId.longName})`}
                </MenuItem>
              ))}
            </Select>
            {validationErrors.selectedVoucherProduct && (
              <Typography variant='caption' color='error'>
                {validationErrors.selectedVoucherProduct}
              </Typography>
            )}
          </FormControl>

          {/* Value Selection */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            {/* Suggested Values Dropdown - only show if product has suggested values and no custom value is being typed */}
            {selectedProduct &&
              selectedProduct.suggestedValues &&
              selectedProduct.suggestedValues.length > 0 &&
              (!formData.voucherValue || selectedSuggestedValue !== 'custom') && (
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Wert</InputLabel>
                  <Select
                    value={selectedSuggestedValue}
                    onChange={e => handleSuggestedValueChange(e.target.value)}
                    label='Wert'
                  >
                    {selectedProduct.suggestedValues.map(value => (
                      <MenuItem key={value} value={value.toString()}>
                        {value.toFixed(2)} €
                      </MenuItem>
                    ))}
                    {selectedProduct.allowCustomValue && (
                      <MenuItem value='custom'>Benutzerdefiniert</MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}

            {/* Voucher Value Field - show if no product selected, or if custom value is selected, or if product has no suggested values */}
            {(!selectedVoucherProduct ||
              (selectedProduct &&
                selectedProduct.allowCustomValue &&
                selectedSuggestedValue === 'custom') ||
              (selectedProduct && !selectedProduct.suggestedValues)) && (
              <TextField
                label={
                  checkRightAny('voucher:special:createPaidVouchers')
                    ? 'Geschenkter Anteil (€)'
                    : 'Wert (€)'
                }
                type='number'
                value={formData.voucherValue}
                onChange={e => handleInputChange('voucherValue', e.target.value)}
                error={!!validationErrors.voucherValue}
                helperText={validationErrors.voucherValue}
                sx={{ flex: 1 }}
                inputProps={{
                  min: selectedProduct?.minValue ? selectedProduct.minValue.toString() : '0.01',
                  max: selectedProduct?.maxValue
                    ? Math.min(selectedProduct.maxValue, 2000).toString()
                    : '2000',
                  step: '0.01',
                }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          </Box>

          {checkRightAny('voucher:special:createPaidVouchers') && (
            <TextField
              label='Bezahlter Anteil (€)'
              type='number'
              value={formData.paidValue}
              onChange={e => handleInputChange('paidValue', e.target.value)}
              error={!!validationErrors.paidValue}
              helperText={validationErrors.paidValue}
              fullWidth
              inputProps={{ min: '0', max: '2000', step: '0.01' }}
              InputLabelProps={{ shrink: true }}
            />
          )}

          <TextField
            label='Ablaufdatum'
            type='date'
            value={formData.expirationDate}
            onChange={e => handleInputChange('expirationDate', e.target.value)}
            error={!!validationErrors.expirationDate}
            helperText={validationErrors.expirationDate || 'Maximal 3 Jahre in der Zukunft'}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: dayjs().format('YYYY-MM-DD'),
              max: maxExpirationDate,
            }}
          />

          {activeTab === 1 && (
            <TextField
              label='Anzahl'
              type='number'
              value={formData.quantity}
              onChange={e => handleInputChange('quantity', e.target.value)}
              error={!!validationErrors.quantity}
              helperText={validationErrors.quantity}
              fullWidth
              inputProps={{ min: '1', max: '10000' }}
            />
          )}

          {/* Optional redemption restriction (branch + entertainments) */}
          <FormControl fullWidth>
            <InputLabel>Einlösbar in (optional)</InputLabel>
            <Select
              value={restrictedBranchId}
              onChange={e => handleRestrictedBranchChange(e.target.value)}
              label='Einlösbar in (optional)'
            >
              <MenuItem value=''>
                <em>Überall einlösbar (keine Einschränkung)</em>
              </MenuItem>
              {branchEntertainments.map(branch => (
                <MenuItem key={branch._id} value={branch._id}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
            <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5 }}>
              Schränkt ein, in welcher Filiale der Gutschein eingelöst werden kann.
            </Typography>
          </FormControl>

          {restrictedBranchId && restrictedEntertainmentOptions.length > 0 && (
            <FormControl fullWidth>
              <InputLabel>Nur für Entertainments (optional)</InputLabel>
              <Select
                multiple
                value={restrictedEntertainmentIds}
                onChange={e =>
                  setRestrictedEntertainmentIds(
                    typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                  )
                }
                label='Nur für Entertainments (optional)'
                renderValue={selected =>
                  selected.length === 0
                    ? 'Alle Entertainments'
                    : restrictedEntertainmentOptions
                        .filter(ent => selected.includes(ent._id))
                        .map(ent => ent.longName)
                        .join(', ')
                }
              >
                {restrictedEntertainmentOptions.map(ent => (
                  <MenuItem key={ent._id} value={ent._id}>
                    {ent.longName}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5 }}>
                Leer = in der gesamten Filiale einlösbar.
              </Typography>
            </FormControl>
          )}

          <TextField
            label='Erstellungskommentar'
            multiline
            rows={3}
            value={formData.creationComment}
            onChange={e => handleInputChange('creationComment', e.target.value)}
            error={!!validationErrors.creationComment}
            helperText={validationErrors.creationComment}
            fullWidth
            required
          />
        </Stack>

        <Stack direction='row' spacing={2} sx={{ mt: 4 }}>
          <Button variant='outlined' onClick={handleClose} fullWidth>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            onClick={handleSubmit}
            disabled={isLoading}
            fullWidth
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? (
              'Wird erstellt...'
            ) : activeTab === 0 ? (
              <LockIconWrapper right='voucher:create'>Gutschein erstellen</LockIconWrapper>
            ) : (
              <LockIconWrapper right='voucher:special:batch'>
                {formData.quantity} Gutscheine erstellen
              </LockIconWrapper>
            )}
          </Button>
        </Stack>
      </Box>
    </Modal>

    <Dialog
      open={zipConfirmOpen}
      onClose={handleZipCancel}
      maxWidth='sm'
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction='row' alignItems='center' spacing={1}>
          <GetAppIcon color='primary' />
          <Typography variant='h6'>PDFs herunterladen</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Es wurden <strong>{zipVoucherIds.length} Gutscheine</strong> erfolgreich erstellt.
        </Typography>
        <Typography color='text.secondary'>
          Möchtest du alle Gutschein-PDFs als ZIP-Datei herunterladen?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleZipCancel} color='inherit'>
          Nein, danke
        </Button>
        <Button
          onClick={handleZipConfirm}
          variant='contained'
          startIcon={<GetAppIcon />}
        >
          ZIP herunterladen
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

export default CreateVoucherModal;
